import type { SearchResultItem } from './GlobalMapSearch.types';
import { getSpatialObjectCenter } from './GlobalMapSearch.utils';
import type { PreparedItem } from './useGroupedResults';

/**
 * Snapshot of a clicked search result that's small enough to persist
 * across sessions but rich enough to (a) re-render the row inside the
 * recent-searches surface with the same icon / label / sublabel as the
 * live result and (b) hand back to `useResultActions` so re-clicking
 * the recent entry runs the same dispatches as clicking the original
 * row would.
 */
export interface RecentSearchResultSnapshot {
  /** Mirrors `PreparedItem.icon` so the dropdown picks the right glyph. */
  icon: PreparedItem['icon'];
  /** Bundled URL for a category-specific silhouette icon, when available. */
  iconUrl?: string;
  label: string;
  sublabel?: string;
  /**
   * Centroid of the result, when one is resolvable. Recent entries
   * with coordinates can drive map pans even from the persisted
   * surface; entries without (e.g. transcript / message rows whose
   * actions only open the CallPane) just rely on the underlying
   * action's own behavior.
   */
  coordinates?: { latitude: number; longitude: number };
  /**
   * The original search-result item, preserved verbatim so
   * `useResultActions.handleResultClick` can re-dispatch the right
   * action when the user re-clicks the recent entry. Plain JSON-able
   * data — safe for redux-persist.
   */
  item: SearchResultItem;
}

/**
 * Persisted recent-search row. Either a query-only entry (the user
 * hit Enter / pressed search) or a result-backed entry (the user
 * clicked a specific row in the dropdown — `result` carries enough
 * info to re-render and re-dispatch).
 */
export interface RecentSearchEntry {
  /**
   * Stable identity for dedup + removal. Result-backed entries encode
   * the underlying record (e.g. `r:call:<callerId>`) so re-clicking the
   * same record collapses; query-only entries collapse case-
   * insensitively (`q:<lowercase>`).
   */
  key: string;
  /** Original typed query string when this entry was created. */
  query: string;
  result?: RecentSearchResultSnapshot;
}

/**
 * Compute a stable identity string for a search-result item so we can
 * dedup the persisted recent list when the same record is clicked
 * across multiple sessions / queries. The exact shape doesn't matter
 * — only stability across re-clicks of the same underlying record.
 */
export function recentKeyForItem(item: SearchResultItem): string {
  switch (item.type) {
    case 'call':
      return `r:call:${item.data.callId}`;
    case 'transcript':
      return `r:transcript:${item.data.callId}`;
    case 'message':
      return `r:message:${item.data.callId}:${item.data.source}`;
    case 'alert':
      return `r:alert:${item.data.alertId}`;
    case 'place': {
      const x = item.data.location?.x ?? '';
      const y = item.data.location?.y ?? '';
      return `r:place:${item.data.address}:${x},${y}`;
    }
    case 'axon': {
      const officer = item.data.attributes?.officer;
      const ext =
        item.data.external_id ||
        officer?.badge_number ||
        officer?.full_name ||
        officer?.last_name ||
        '';
      return `r:axon:${ext}`;
    }
    case 'static':
    case 'dynamic': {
      const so = item.data.spatialObject as Record<string, unknown>;
      const id =
        (so?.external_id as string | undefined) ||
        (so?.id as string | number | undefined) ||
        (so?.name as string | undefined) ||
        (so?.title as string | undefined) ||
        '';
      return `r:${item.type}:${item.data.layerKey}:${id}`;
    }
    case 'gis': {
      const c = item.data.geometry?.centroid;
      const coord = c ? `${c.latitude},${c.longitude}` : '';
      return `r:gis:${item.data.sourceTitle}:${item.data.kind}:${coord}`;
    }
    default:
      // Exhaustiveness fallback — produces a unique-ish key without
      // pulling private fields off an unknown variant.
      return `r:unknown:${JSON.stringify(item).slice(0, 64)}`;
  }
}

/**
 * Resolve the best-effort centroid for a result so the persisted
 * snapshot can carry coordinates without re-running the source-
 * specific extraction at render time.
 */
function coordinatesForItem(
  item: SearchResultItem,
): { latitude: number; longitude: number } | undefined {
  if (item.type === 'place' && item.data.location) {
    return { latitude: item.data.location.y, longitude: item.data.location.x };
  }
  if (item.type === 'axon') {
    const c = item.data.shape?.features?.[0]?.geometry?.coordinates;
    if (c && typeof c[0] === 'number' && typeof c[1] === 'number') {
      return { latitude: c[1], longitude: c[0] };
    }
  }
  if (item.type === 'static' || item.type === 'dynamic') {
    const c = getSpatialObjectCenter(item.data.spatialObject);
    if (c) return c;
  }
  if (item.type === 'gis') {
    const c = item.data.geometry?.centroid;
    if (c) return c;
  }
  return undefined;
}

/**
 * Build a persisted recent-search entry from a freshly-clicked
 * `PreparedItem` (the dropdown's render-ready shape) plus the user's
 * typed query at the moment of the click. Pure / side-effect free —
 * meant to be passed straight into `addRecentSearch`.
 */
export function buildRecentEntryFromPrepared(
  query: string,
  prepared: PreparedItem,
): RecentSearchEntry {
  const item = prepared.item;
  return {
    key: recentKeyForItem(item),
    query,
    result: {
      icon: prepared.icon,
      iconUrl: prepared.iconUrl,
      label: prepared.label,
      sublabel: prepared.sublabel || undefined,
      coordinates: coordinatesForItem(item),
      item,
    },
  };
}
